import { api } from './apiClient';

// Types are intentionally light; the API is the source of truth.
export interface CommandCenter {
  asOf: string; financialYear: string;
  position: Record<string, number>;
  profitability: Record<string, number>;
  liquidity: Record<string, number>;
  workingCapital: Record<string, number>;
  tax: Record<string, number>;
  exceptions: {
    overdueReceivables: { count: number; amount: number };
    overduePayables: { count: number; amount: number };
    unpostedJournals: number; pendingApprovals: number;
  };
}

export const financeWorkspaceService = {
  commandCenter: (): Promise<CommandCenter> => api.get('/finance-command-center'),
  operationsCommandCenter: (): Promise<any> => api.get('/operations-command-center'),
  salesControl: (): Promise<any> => api.get('/sales-control'),
  procurementControl: (): Promise<any> => api.get('/procurement-control'),
  inventoryControl: (): Promise<any> => api.get('/inventory-control'),
  qualityControl: (): Promise<any> => api.get('/quality-control'),
  maintenanceControl: (): Promise<any> => api.get('/maintenance-control'),
  customer360: (id: number): Promise<any> => api.get(`/entity/customer/${id}`),
  supplier360: (id: number): Promise<any> => api.get(`/entity/supplier/${id}`),
  material360: (id: number): Promise<any> => api.get(`/entity/material/${id}`),
  machine360: (id: number): Promise<any> => api.get(`/entity/machine/${id}`),
  globalSearch: (q: string): Promise<{ query: string; results: any[] }> => api.get('/global-search', { params: { q } }),
  moneyMovement: (): Promise<any> => api.get('/money-movement'),
  demoStory: (): Promise<any> => api.get('/demo-story'),

  // Masters
  listCostCenters: (): Promise<{ costCenters: any[] }> => api.get('/cost-centers'),
  createCostCenter: (d: any) => api.post('/cost-centers', d),
  updateCostCenter: (id: number, d: any) => api.put(`/cost-centers/${id}`, d),
  listProfitCenters: (): Promise<{ profitCenters: any[] }> => api.get('/profit-centers'),
  createProfitCenter: (d: any) => api.post('/profit-centers', d),
  updateProfitCenter: (id: number, d: any) => api.put(`/profit-centers/${id}`, d),

  // Budgets
  listBudgets: (): Promise<{ budgets: any[] }> => api.get('/budgets'),
  getBudget: (id: number): Promise<{ budget: any }> => api.get(`/budgets/${id}`),
  createBudget: (d: any) => api.post('/budgets', d),
  budgetVsActual: (id: number): Promise<{ budget: any; rows: any[]; totals: any }> => api.get(`/budgets/${id}/vs-actual`),

  // Templates + recurring
  listTemplates: (): Promise<{ templates: any[] }> => api.get('/journal-templates'),
  getTemplate: (id: number): Promise<{ template: any }> => api.get(`/journal-templates/${id}`),
  createTemplate: (d: any) => api.post('/journal-templates', d),
  listRecurring: (): Promise<{ recurring: any[] }> => api.get('/recurring-journals'),
  createRecurring: (d: any) => api.post('/recurring-journals', d),
  generateRecurring: (id: number) => api.post(`/recurring-journals/${id}/generate`),
  runDueRecurring: () => api.post('/recurring-journals/run-due'),

  // Period close
  listPeriods: (financialYear?: string): Promise<{ periods: any[] }> =>
    api.get('/period-close/periods', { params: financialYear ? { financialYear } : undefined }),
  closeChecklist: (financialYear: string, periodNumber: number): Promise<any> =>
    api.get('/period-close/checklist', { params: { financialYear, periodNumber } }),
  closePeriod: (d: any) => api.post('/period-close/close', d),
  reopenPeriod: (d: any) => api.post('/period-close/reopen', d),

  // Statements + collections
  customerStatement: (id: number, params?: any): Promise<any> => api.get(`/statements/customer/${id}`, { params }),
  supplierStatement: (id: number, params?: any): Promise<any> => api.get(`/statements/supplier/${id}`, { params }),
  collections: (): Promise<any> => api.get('/collections'),

  // Credit / Debit notes
  listCreditNotes: (): Promise<{ creditNotes: any[] }> => api.get('/credit-notes'),
  getCreditNote: (id: number): Promise<{ creditNote: any }> => api.get(`/credit-notes/${id}`),
  createCreditNote: (d: any) => api.post('/credit-notes', d),
  listDebitNotes: (): Promise<{ debitNotes: any[] }> => api.get('/debit-notes'),
  createDebitNote: (d: any) => api.post('/debit-notes', d),
};

export default financeWorkspaceService;
